import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Headphones, Music, ExternalLink, Calendar, MapPin, Building2 } from 'lucide-react';
import { API } from '../config/api';



export default function EngineerPage() {
  const { engineerId } = useParams();
  const [engineer, setEngineer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEngineer();
  }, [engineerId]);

  const fetchEngineer = async () => {
    try {
      const response = await fetch(`${API}/engineers/${engineerId}`);
      if (response.ok) {
        setEngineer(await response.json());
      } else {
        setError('Engineer not found');
      }
    } catch (err) {
      setError('Failed to load engineer');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !engineer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="glass-card p-8 text-center max-w-md">
          <Headphones className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-heading text-2xl font-bold mb-2">Engineer Not Found</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/creatives">
            <Button>Back to Creatives</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="engineer-page">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="relative mb-8">
          <div className="h-48 sm:h-64 rounded-xl bg-gradient-to-br from-blue-900/50 to-cyan-900/50 overflow-hidden">
            {engineer.banner_url && (
              <img src={engineer.banner_url} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 -mt-16 sm:-mt-20 px-4 sm:px-6">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl bg-blue-500/20 border-4 border-background flex items-center justify-center overflow-hidden">
              {engineer.avatar_url ? (
                <img src={engineer.avatar_url} alt={engineer.display_name} className="w-full h-full object-cover" />
              ) : (
                <Headphones className="w-16 h-16 text-blue-400" />
              )}
            </div>
            <div className="flex-1 pt-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold" data-testid="engineer-name">
                  {engineer.display_name}
                </h1>
                {engineer.is_verified && (
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Verified</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
                <span className="flex items-center gap-1">
                  <Music className="w-4 h-4" /> {engineer.credits_count || 0} credits
                </span>
                {engineer.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {engineer.location}
                  </span>
                )}
                {engineer.studio_name && (
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4" /> {engineer.studio_name}
                  </span>
                )}
              </div>
              {engineer.booking_link && (
                <a href={engineer.booking_link} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Calendar className="w-4 h-4 mr-2" /> Book Session
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Tabs defaultValue="about">
              <TabsList className="bg-muted/50 mb-6">
                <TabsTrigger value="about">About</TabsTrigger>
                <TabsTrigger value="discography">Discography</TabsTrigger>
              </TabsList>

              <TabsContent value="about">
                <Card className="glass-card border-border p-6">
                  <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Bio</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {engineer.bio || 'No bio provided yet.'}
                  </p>
                  
                  {engineer.specialties && engineer.specialties.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Specialties</h3>
                      <div className="flex flex-wrap gap-2">
                        {engineer.specialties.map((specialty, i) => (
                          <Badge key={i} variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-400">
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {engineer.daw_proficiency && engineer.daw_proficiency.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">DAW Proficiency</h3>
                      <div className="flex flex-wrap gap-2">
                        {engineer.daw_proficiency.map((daw, i) => (
                          <Badge key={i} variant="outline">{daw}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="discography">
                <Card className="glass-card border-border p-8 text-center">
                  <Music className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Discography coming soon</p>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="space-y-6">
            <Card className="glass-card border-border p-4">
              <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Credits</span>
                  <span className="font-bold">{engineer.credits_count || 0}</span>
                </div>
                {engineer.rate_range && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rate</span>
                    <span className="font-bold text-green-400">{engineer.rate_range}</span>
                  </div>
                )}
              </div>
            </Card>

            {engineer.studio_affiliation && (
              <Card className="glass-card border-border p-4">
                <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Studio</h3>
                <Link to={`/studios/${engineer.studio_affiliation}`}>
                  <Button variant="outline" className="w-full">
                    <Building2 className="w-4 h-4 mr-2" /> {engineer.studio_name || 'View Studio'}
                  </Button>
                </Link>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
